/**
 * PROC-000054 item 2 (Ashok UX requirement), layout-corrected per Ashok's
 * follow-up spec: TWO stacked boxes, not one -
 *   BOX 1 "Your Request" - the original user_requirement text only.
 *   BOX 2 "Parsed Details" - a FIXED layout, not the generic dynamic grid:
 *     Row 1, three equal columns: Category · Quantity · Procurement Type
 *     Row 2: Scope, full width
 *     Row 3: Objective, full width
 * Same fields/values as before, ONE collapse control for both boxes (kept
 * simple, per the spec - not a per-box toggle). Collapse state remembered
 * per procurement for the session (sessionStorage).
 *
 * Reuses ProcurementDetail's parsed-requirement VALUE formatter
 * (formatParsedRequirementFields - category-lookup resolution, N/A
 * fallback, nested-shape handling) rather than a second copy - single-
 * source presentation (Principle 10). Only the ARRANGEMENT into this
 * fixed 3-row layout is specific to this card; ProcurementDetail's own
 * "Parsed Requirement" panel keeps using the generic dynamic grid
 * (ParsedRequirementGrid) - out of scope for this wizard-steps-only card.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import {
  formatParsedRequirementFields,
  useCategoryLookup,
  getCategoryConfidence,
  getRawCategoryCode,
  getCategoryNoFit,
  getCategoryNoFitSubject,
} from '@/components/ParsedRequirementFields';
import CategoryConfirmRequired from './CategoryConfirmRequired';
import { retiredHeaderCode } from '@/lib/masterReferenceStaleness';
import { categoryGateDecisionContext } from '@/lib/categoryGateDecisionContext';
import CategoryChangeControl from './CategoryChangeControl';

const storageKeyFor = (procurementId) => `wizard-context-card-collapsed-${procurementId || 'unknown'}`;

const ParsedDetailField = ({ field }) => {
  if (!field) return null;
  return (
    <div className="bg-zinc-50 p-3 rounded">
      <div className="text-xs text-zinc-500 mb-1">{field.label}</div>
      <div className="text-sm text-zinc-900 font-medium break-words">{field.value}</div>
    </div>
  );
};

// Case 1 change affordance (Ashok ruling, 2026-08-04). allowCategoryChange
// is OPT-IN, default false, and every caller that wants the "change" link
// must say so explicitly.
//
// WHY OPT-IN RATHER THAN ALWAYS-ON: an affordance may only render where its
// action has no un-cascaded downstream effect. This card renders at FIVE
// call sites across three steps (StepIntake x1, StepQuestions x2,
// StepAnswers x2 - StepIntake was StepUploads until Intake Compression
// V1 merged the two, 2026-08-20, and its editable branch stopped rendering
// the card at all, since a draft has no parse to summarise; the count was
// six before that). At Intake nothing downstream exists
// yet, so changing the category costs nothing. At Questions and Answers the questions were
// already GENERATED FROM the old category, and the category_override path
// deliberately fires no staleness cascade (WizardContext's
// handleCategoryOverride) - offering the link there would silently leave
// artifacts derived from a category the buyer just discarded. Changing a
// category once questions exist is a RE-PARSE, a separate feature. So the
// default is the safe one and the two safe sites opt in, rather than the
// default being live everywhere and four sites remembering to opt out.
// suppressRequirementEcho (2026-08-21): OPT-IN, default false, same shape
// and same reasoning as allowCategoryChange above - a caller that wants the
// non-default must say so, so adding a consumer cannot change behaviour by
// omission.
//
// WHAT IT SUPPRESSES, AND WHAT IT MUST NOT. Only Box 1's requirement
// paragraph. The Box 1 HEADER stays: it is the single collapse control for
// BOTH boxes, so removing it would take the parsed details' toggle with it.
// Box 2 - category and its change link, quantity, procurement type, scope,
// objective - is untouched on every path.
//
// WHY ONLY ONE STEP OPTS IN. On Questions and Answers this card echoes a
// requirement the buyer typed on an EARLIER step, which is the point of it
// being there - they cannot see the original without scrolling back through
// the wizard. On the merged Intake step the requirement is on the SAME
// screen, in the step's own "Your Requirement" section, so the echo became
// a duplicate the moment Uploads was absorbed (Intake Compression V1). It
// was never redundant before; the merge made it so.
const RequirementContextCard = ({ procurementId, userRequirement, parsedRequirement, categoryActiveResolvable = null, onCategoryConfirm, onCategoryDeclareNoFit = null, allowCategoryChange = false, suppressRequirementEcho = false, masterReferenceAdvisory = null }) => {
  const categoryLookup = useCategoryLookup();
  const [changingCategory, setChangingCategory] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem(storageKeyFor(procurementId)) === '1';
    } catch {
      return false;
    }
  });

  if (!userRequirement && !parsedRequirement) return null;

  const toggle = () => {
    setCollapsed(prev => {
      const next = !prev;
      try {
        sessionStorage.setItem(storageKeyFor(procurementId), next ? '1' : '0');
      } catch {
        // sessionStorage unavailable (private browsing etc.) - collapse
        // state just won't survive a remount; not worth failing over.
      }
      return next;
    });
  };

  const fields = formatParsedRequirementFields(parsedRequirement, categoryLookup);
  const fieldByKey = Object.fromEntries(fields.map((f) => [f.key, f]));
  const category = fieldByKey.category;
  const quantity = fieldByKey.quantity;
  const procurementType = fieldByKey.procurement_type;
  const scope = fieldByKey.scope;
  const objective = fieldByKey.objective;
  const hasParsedDetails = Boolean(category || quantity || procurementType || scope || objective);

  // Only replace the passive category display with the forced-selection
  // gate when a handler was actually wired in - a step that hasn't been
  // updated to pass onCategoryConfirm falls back to the ordinary passive
  // display rather than showing a non-functional selector.
  //
  // TWO REASONS TO DEMAND A SELECTION, and the OR must match StepIntake's
  // `categorySelectionRequired` exactly - that one disables Continue, this
  // one renders the control the buyer needs. If they disagree, the buyer
  // is either blocked with no control or offered a control while free to
  // walk past it. See StepIntake for what each condition covers and why
  // neither implies the other. (Was StepUploads until Intake Compression
  // V1, 2026-08-20, which merged that step into Intake; the pair and its
  // reasoning moved together, unchanged.)
  //
  // === false, NOT falsy: null means the master could not be read, which
  // is not the buyer's problem to solve. Same rule, same reason, as the
  // Continue gate.
  // THE HEADER NO-FIT (F1 / C1b-1b). A THIRD reason to demand a selection,
  // and it must appear in StepIntake's `categorySelectionRequired` too -
  // see the note above and CLAUDE.md's mirror-pair table, where this pair
  // is registered.
  //
  // In practice the no-fit branch also drives categoryActiveResolvable to
  // false (declaring one clears the decided category, so nothing resolves),
  // so the gate would open without this term. It is stated ANYWAY, and not
  // as belt-and-braces: this component must be able to tell a no-fit from
  // an unresolvable code, because the two get DIFFERENT COPY. Inferring
  // "the buyer declared nothing fits" from "the stored code does not
  // resolve" is exactly the inference this codebase keeps paying for.
  const categoryNoFit = getCategoryNoFit(parsedRequirement);
  const categoryNeedsSelection = onCategoryConfirm && (
    getCategoryConfidence(parsedRequirement) === 'none'
    || categoryActiveResolvable === false
    || Boolean(categoryNoFit)
  );
  const rawCategoryCode = categoryNeedsSelection ? getRawCategoryCode(parsedRequirement) : null;

  // The gate and the change link are MUTUALLY EXCLUSIVE by construction:
  // the gate demands a category that isn't decided, the link offers to
  // revise one that is. !categoryNeedsSelection is what keeps them from
  // ever rendering together.
  const showCategoryChange = Boolean(allowCategoryChange && onCategoryConfirm && !categoryNeedsSelection && category);

  const showBox2 = !collapsed && hasParsedDetails;

  return (
    <div data-testid="requirement-context-card" className="mb-6">
      {/* Box 1 - "Your Request". Its header doubles as the ONE collapse
          control for both boxes, per the spec. */}
      <div className={`bg-blue-50 border border-blue-200 rounded-lg ${showBox2 ? 'mb-3' : ''}`}>
        <button
          type="button"
          onClick={toggle}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
          data-testid="requirement-context-toggle"
          aria-expanded={!collapsed}
        >
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-900">
            <FileText className="w-3.5 h-3.5" />
            Your Request
          </span>
          {collapsed ? (
            <ChevronDown className="w-4 h-4 text-blue-600 flex-shrink-0" />
          ) : (
            <ChevronUp className="w-4 h-4 text-blue-600 flex-shrink-0" />
          )}
        </button>
        {/* The echo. Suppressed only where the same text is already on
            screen - see suppressRequirementEcho's declaration above. The
            header ABOVE this stays either way; it is the collapse control
            for the parsed-details box too. */}
        {!collapsed && !suppressRequirementEcho && userRequirement && (
          <div className="px-4 pb-4" data-testid="requirement-context-echo">
            <p className="text-sm text-blue-900/90 leading-relaxed">{userRequirement}</p>
          </div>
        )}
      </div>

      {/* Box 2 - "Parsed Details", fixed 3-row layout. */}
      {showBox2 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg">
          <div className="px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-blue-900">
            Parsed Details
          </div>
          <div className="px-4 pb-4 space-y-4">
            {categoryNeedsSelection && (
              <CategoryConfirmRequired
                userRequirement={userRequirement}
                suggestedCategoryCode={rawCategoryCode}
                /* Declares the event on the buyer's behalf at the call
                   site, so CategoryConfirmRequired itself stays untouched.
                   THE CONTEXT MATCHES WHY THE GATE IS OPEN (2026-09-16):
                   forced_low_signal for a zero-floor pick, retired_category_
                   replaced for a header the master retired. This used to be
                   a hard-coded forced_low_signal, which the server refuses
                   for a retired incumbent - every selection 400'd and the
                   buyer was trapped (PROC-000353). See
                   lib/categoryGateDecisionContext.js. */
                onConfirm={(code) => onCategoryConfirm(code, categoryGateDecisionContext({
                  confidence: getCategoryConfidence(parsedRequirement),
                  categoryActiveResolvable,
                }))}
                /* NO-FIT (F1 / C1b-1b). The subject is the unwrapped
                   parsed_requirement, so the picker reads the record and
                   its request number from the same object update-intake
                   writes it to. Withdrawal needs no handler here:
                   reselecting sends a category_override, and the endpoint
                   deletes the record in the same write as the category it
                   contradicts - one writer, not two. */
                noFitSubject={getCategoryNoFitSubject(parsedRequirement)}
                onDeclareNoFit={onCategoryDeclareNoFit}
                noFitRecord={categoryNoFit}
                /* RETIRED INCUMBENT (staleness gate, 2026-09-15): the header
                   code the SERVER's advisory names as no longer active. Copy
                   only - it does not enter categoryNeedsSelection's OR (the
                   registered mirror pair with StepIntake is untouched);
                   categoryActiveResolvable === false already opens the gate. */
                retiredCategoryCode={retiredHeaderCode(masterReferenceAdvisory)}
              />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {!categoryNeedsSelection && !showCategoryChange && <ParsedDetailField field={category} />}
              {showCategoryChange && (
                <div className="bg-zinc-50 p-3 rounded">
                  <div className="text-xs text-zinc-500 mb-1">{category.label}</div>
                  <div className="text-sm text-zinc-900 font-medium break-words">
                    {/* The value stays PLAIN TEXT - only "change" is a
                        control, and it is visually secondary to the
                        category it acts on. */}
                    {category.value}
                    {!changingCategory && (
                      <>
                        {' · '}
                        <button
                          type="button"
                          onClick={() => setChangingCategory(true)}
                          className="text-xs font-normal text-zinc-500 underline hover:text-zinc-700"
                          data-testid="category-change-link"
                        >
                          change
                        </button>
                      </>
                    )}
                  </div>
                  {changingCategory && (
                    <CategoryChangeControl
                      userRequirement={userRequirement}
                      currentCategoryCode={getRawCategoryCode(parsedRequirement)}
                      /* The passive change control offers the declaration
                         too: a buyer who disagrees with a confident pick
                         may find that nothing else fits either, and
                         sending them to look for a category that is not
                         there is how the false record gets made. */
                      noFitSubject={getCategoryNoFitSubject(parsedRequirement)}
                      onDeclareNoFit={onCategoryDeclareNoFit}
                      onChange={async (code) => {
                        await onCategoryConfirm(code, 'voluntary_change');
                        setChangingCategory(false);
                      }}
                      /* Cancel closes the control and NOTHING else - no
                         request, no state write. */
                      onCancel={() => setChangingCategory(false)}
                    />
                  )}
                </div>
              )}
              <ParsedDetailField field={quantity} />
              <ParsedDetailField field={procurementType} />
            </div>
            <ParsedDetailField field={scope} />
            <ParsedDetailField field={objective} />
          </div>
        </div>
      )}
    </div>
  );
};

export default RequirementContextCard;
